/**
 * Rewards & Wallet page.
 *
 * Three stacked sections:
 *   1. Balance hero   — available points (≈ ₹ value) + Redeem CTA + expiring nudge.
 *   2. How it works   — earn → redeem (≤ 50% of fee) → save (1 pt = ₹1).
 *   3. Ledger         — paginated transaction history (credits + redemptions).
 *
 * Balance comes from /rewards/wallet; the ledger from the paginated
 * /rewards/transactions endpoint so Prev/Next stay clean. Reward amounts are
 * never hard-coded in copy — points and ₹ values come from the API.
 */
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Clock,
  Coins,
  Gift,
  Receipt,
  Sparkles,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorMessage,
  Skeleton,
} from '@finmin/ui';
import { formatCurrency } from '@finmin/utils';
import { useRewardTransactions, useRewardWallet } from '@/api/rewards';
import { TransactionItem } from './components/transaction-item';
import { RedeemPointsDialog } from './components/redeem-points-dialog';
import { findExpiringSoon, formatPoints, pointsToInr } from './utils';

const PALETTE_FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const PALETTE_MINT   = 'hsl(var(--palette-mint,   154  38% 94%))';
const PALETTE_AMBER  = 'hsl(var(--palette-amber,   33 100% 50%))';
const PALETTE_CREAM  = 'hsl(var(--palette-cream,   40 100% 98%))';

const PAGE_SIZE = 10;

export function RewardsPage() {
  const walletQ = useRewardWallet();
  const [page, setPage] = React.useState(1);
  const txQ = useRewardTransactions(page, PAGE_SIZE);
  const [redeemOpen, setRedeemOpen] = React.useState(false);

  const balance = walletQ.data?.available_balance ?? 0;
  const expiring = React.useMemo(
    () => findExpiringSoon(walletQ.data?.transactions ?? []),
    [walletQ.data?.transactions],
  );

  const transactions = txQ.data?.transactions ?? [];
  const total = txQ.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <main className="mx-auto max-w-3xl px-6 py-10 space-y-8">

      {/* Back nav */}
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group"
      >
        <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" aria-hidden />
        Back to Dashboard
      </Link>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div
          className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: PALETTE_MINT }}
          aria-hidden
        >
          <Coins className="h-6 w-6" style={{ color: PALETTE_FOREST }} />
        </div>
        <div className="min-w-0">
          <h1 className="font-display font-medium text-2xl tracking-tight text-foreground">
            Rewards &amp; Wallet
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5 max-w-xl">
            Earn points from referrals and bonuses, then redeem them for a discount
            on any service fee. 1 point = ₹1 off.
          </p>
        </div>
      </div>

      {/* ── Balance hero ─────────────────────────────────────────────────── */}
      <div
        className="relative overflow-hidden rounded-2xl p-6 sm:p-7"
        style={{ backgroundColor: PALETTE_FOREST }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full blur-3xl opacity-20 animate-float-slow"
          style={{ backgroundColor: PALETTE_AMBER }}
        />
        <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
          <div className="min-w-0">
            <p
              className="text-[10px] font-bold uppercase tracking-widest"
              style={{ color: PALETTE_AMBER }}
            >
              Available balance
            </p>
            {walletQ.isLoading ? (
              <Skeleton className="h-12 w-40 mt-2 rounded-lg" />
            ) : (
              <>
                <p
                  className="font-display font-semibold text-4xl sm:text-5xl tracking-tight mt-1 tabular-nums"
                  style={{ color: PALETTE_CREAM }}
                >
                  {formatPoints(balance)}{' '}
                  <span className="text-2xl sm:text-3xl font-medium">pts</span>
                </p>
                <p
                  className="text-sm mt-1"
                  style={{ color: 'hsl(var(--palette-cream, 40 100% 98%) / 0.75)' }}
                >
                  ≈ {formatCurrency(pointsToInr(balance))} in service discounts
                </p>
              </>
            )}
          </div>

          <div className="shrink-0 flex items-center gap-2">
            <Link
              to="/referrals"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all hover:bg-white/10 active:scale-95 border"
              style={{ color: PALETTE_CREAM, borderColor: 'rgba(255,255,255,0.2)' }}
            >
              <Gift className="h-4 w-4" aria-hidden />
              Earn more
            </Link>
            <button
              type="button"
              onClick={() => setRedeemOpen(true)}
              disabled={walletQ.isLoading || balance < 1}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all hover:shadow-md active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: PALETTE_AMBER }}
            >
              <Sparkles className="h-4 w-4" aria-hidden />
              Redeem points
            </button>
          </div>
        </div>

        {/* Expiring-soon nudge */}
        {expiring && (
          <div
            className="relative mt-5 flex items-center gap-2 rounded-xl px-3 py-2 text-sm"
            style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: PALETTE_CREAM }}
          >
            <Clock className="h-4 w-4 shrink-0" style={{ color: PALETTE_AMBER }} aria-hidden />
            <span>
              <span className="font-semibold">{formatPoints(expiring.points)} pts</span> expire in{' '}
              {expiring.days} {expiring.days === 1 ? 'day' : 'days'} — redeem them before they lapse.
            </span>
          </div>
        )}
      </div>

      {walletQ.error && <ErrorMessage message={walletQ.error.message} />}

      {/* ── How it works ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <HowItWorks
          icon={<Gift className="h-4 w-4" />}
          title="Earn"
          desc="Get points from referrals and FinminOS bonuses."
        />
        <HowItWorks
          icon={<TrendingUp className="h-4 w-4" />}
          title="Redeem"
          desc="Apply points to a service — up to 50% of its fee."
        />
        <HowItWorks
          icon={<Wallet className="h-4 w-4" />}
          title="Save"
          desc="1 point = ₹1 off. The discount lands instantly."
        />
      </div>

      {/* ── Ledger ───────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Receipt className="h-4 w-4 text-muted-foreground" aria-hidden />
            Transaction history
            {total > 0 && (
              <Badge variant="secondary" className="ml-1">{total}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {txQ.error ? (
            <ErrorMessage message={txQ.error.message} />
          ) : txQ.isLoading ? (
            <LedgerSkeleton />
          ) : transactions.length === 0 ? (
            <div className="py-10 text-center">
              <div
                className="mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-3"
                style={{ backgroundColor: PALETTE_MINT }}
              >
                <Receipt className="h-6 w-6" style={{ color: PALETTE_FOREST }} aria-hidden />
              </div>
              <p className="text-sm font-medium text-foreground">No transactions yet</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Refer a friend to earn your first reward points.
              </p>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-border -my-2" aria-busy={txQ.isFetching}>
                {transactions.map((tx) => (
                  <TransactionItem key={tx.id} tx={tx} />
                ))}
              </ul>

              {/* Pagination */}
              {pageCount > 1 && (
                <div className="flex items-center justify-between pt-4 mt-2 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    Page {page} of {pageCount}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1 || txQ.isFetching}
                    >
                      <ChevronLeft className="h-4 w-4" /> Prev
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                      disabled={page >= pageCount || txQ.isFetching}
                    >
                      Next <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <RedeemPointsDialog
        open={redeemOpen}
        onClose={() => setRedeemOpen(false)}
        availableBalance={balance}
      />
    </main>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function HowItWorks({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="rounded-xl border border-border bg-background/60 p-3">
      <span
        className="inline-flex h-7 w-7 items-center justify-center rounded-lg"
        style={{ backgroundColor: PALETTE_MINT, color: PALETTE_FOREST }}
        aria-hidden
      >
        {icon}
      </span>
      <p className="text-sm font-semibold text-foreground mt-2">{title}</p>
      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>
    </div>
  );
}

function LedgerSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="flex-1">
            <Skeleton className="h-4 w-40 mb-1.5" />
            <Skeleton className="h-3 w-28" />
          </div>
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
}
